import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api, { endpoints } from '../../utils/apiClient';
import { format } from 'date-fns';

const AdminTasks = () => {
  // State
  const [tasks, setTasks] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submissionsLoading, setSubmissionsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [currentSubmission, setCurrentSubmission] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'tweet',
    reward: {
      amount: 50,
      currency: 'BAI'
    },
    maxParticipants: 100,
    startDate: new Date(),
    endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)),
    tweetUrl: '',
    tweetAuthor: '',
    requirements: {
      like: true,
      retweet: true,
      quote: false
    },
    notifyBotUsers: true // Default to true for sending notifications
  });

  // Fetch tasks and submissions on component mount
  useEffect(() => {
    fetchTasks();
    fetchSubmissions();
  }, []);

  // Fetch all tasks
  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await api.get(endpoints.admin.tasks.list);
      setTasks(Array.isArray(response) ? response : response.data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all submissions
  const fetchSubmissions = async () => {
    try {
      setSubmissionsLoading(true);
      const response = await api.get(endpoints.admin.tasks.submissions);
      setSubmissions(Array.isArray(response) ? response : response.data || []);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to fetch submissions');
    } finally {
      setSubmissionsLoading(false);
    }
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Handle date change
  const handleDateChange = (field, date) => {
    setFormData(prev => ({
      ...prev,
      [field]: date
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      type: 'tweet',
      reward: {
        amount: 50,
        currency: 'BAI'
      },
      maxParticipants: 100,
      startDate: new Date(),
      endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)),
      tweetUrl: '',
      tweetAuthor: '',
      requirements: {
        like: true,
        retweet: true,
        quote: false
      },
      notifyBotUsers: true
    });
    setCurrentTask(null);
  };

  // Open edit modal
  const handleEdit = (task) => {
    setCurrentTask(task);
    setFormData({
      title: task.title,
      description: task.description,
      type: task.type,
      reward: {
        amount: task.reward.amount,
        currency: task.reward.currency
      },
      maxParticipants: task.maxParticipants,
      startDate: new Date(task.startDate),
      endDate: new Date(task.endDate),
      tweetUrl: task.tweetUrl || '',
      tweetAuthor: task.tweetAuthor || '',
      requirements: {
        like: task.requirements?.like || false,
        retweet: task.requirements?.retweet || false,
        quote: task.requirements?.quote || false
      },
      // Default to true for notifications when editing
      notifyBotUsers: true
    });
    setShowCreateModal(true);
  };

  // Handle task submission
  const handleSubmitTask = async (e) => {
    e.preventDefault();

    try {
      // Handle numeric values
      const submitData = {
        ...formData,
        reward: {
          ...formData.reward,
          amount: Number(formData.reward.amount)
        },
        maxParticipants: Number(formData.maxParticipants)
      };

      if (currentTask) {
        // Update existing task
        await api.put(endpoints.admin.tasks.update(currentTask._id), submitData);
        toast.success('Task updated successfully');
      } else {
        // Create new task
        await api.post(endpoints.admin.tasks.create, submitData);
        toast.success('Task created successfully');
      }

      // Refresh tasks
      fetchTasks();

      // Close modal and reset form
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      console.error('Error submitting task:', error);
      toast.error('Failed to save task');
    }
  };

  // Handle task deletion
  const handleDelete = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      const response = await api.delete(endpoints.admin.tasks.delete(taskId));

      // Check if the task was cancelled instead of deleted
      if (response.data && response.data.task && response.data.task.status === 'cancelled') {
        toast.info(response.data.message || 'Task was marked as cancelled due to existing submissions');
      } else {
        toast.success(response.data.message || 'Task deleted successfully');
      }

      fetchTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error(error.response?.data?.message || 'Failed to delete task');
    }
  };

  // Open submission modal
  const handleViewSubmission = (submission) => {
    setCurrentSubmission(submission);
    setAdminNotes('');
    setShowSubmissionModal(true);
  };

  // Handle submission approval
  const handleApproveSubmission = async () => {
    try {
      await api.post(endpoints.admin.tasks.approveSubmission(currentSubmission._id), {
        adminNotes
      });
      toast.success('Submission approved successfully');

      // Refresh both submissions and tasks to ensure UI is up-to-date
      fetchSubmissions();
      fetchTasks();

      setShowSubmissionModal(false);
    } catch (error) {
      console.error('Error approving submission:', error);
      toast.error('Failed to approve submission');
    }
  };

  // Handle submission rejection
  const handleRejectSubmission = async () => {
    try {
      await api.post(endpoints.admin.tasks.rejectSubmission(currentSubmission._id), {
        adminNotes
      });
      toast.success('Submission rejected successfully');
      fetchSubmissions();
      setShowSubmissionModal(false);
    } catch (error) {
      console.error('Error rejecting submission:', error);
      toast.error('Failed to reject submission');
    }
  };

  // Filter tasks by search term
  const filteredTasks = tasks.filter(task =>
    task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    task.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter pending submissions
  const pendingSubmissions = submissions.filter(sub => sub.status === 'pending');

  return (
    <div>
      {/* Tasks Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Tasks</h2>
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition duration-200"
          >
            Create Task
          </button>
        </div>

        <div className="mb-4">
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="bg-gray-800 rounded-lg p-4 text-center">
            <p className="text-gray-400">No tasks found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredTasks.map(task => (
              <div key={task._id} className="bg-gray-800 rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-semibold">{task.title}</h3>
                  <span className="px-2 py-1 bg-blue-900 text-blue-300 text-xs rounded-full">
                    {task.type.toUpperCase()}
                  </span>
                </div>

                <p className="text-gray-400 text-sm mb-3">{task.description}</p>

                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div>
                    <span className="text-gray-400 text-xs">Reward</span>
                    <p>{task.reward.amount} {task.reward.currency}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs">Participants</span>
                    <p>{task.currentParticipants || 0}/{task.maxParticipants}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs">Start Date</span>
                    <p>{format(new Date(task.startDate), 'MMM d, yyyy')}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs">End Date</span>
                    <p>{format(new Date(task.endDate), 'MMM d, yyyy')}</p>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(task)}
                    className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm transition duration-200"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(task._id)}
                    className="px-3 py-1 bg-red-900 hover:bg-red-800 text-red-300 rounded-lg text-sm transition duration-200"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Submissions Section */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Pending Submissions</h2>
          <span className="px-3 py-1 bg-yellow-900 text-yellow-300 rounded-full text-sm">
            {pendingSubmissions.length} Pending
          </span>
        </div>

        {submissionsLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500"></div>
          </div>
        ) : pendingSubmissions.length === 0 ? (
          <div className="bg-gray-800 rounded-lg p-4 text-center">
            <p className="text-gray-400">No pending submissions</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pendingSubmissions.map(submission => {
              const task = tasks.find(t => t._id === submission.taskId);
              return (
                <div key={submission._id} className="bg-gray-800 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-semibold">
                      {submission.username || `User ${submission.userId.slice(0, 8)}`}
                    </h3>
                    <span className="px-2 py-1 bg-yellow-900 text-yellow-300 text-xs rounded-full">
                      PENDING
                    </span>
                  </div>

                  <p className="text-gray-400 text-sm mb-3">
                    Task: {task ? task.title : 'Unknown Task'}
                  </p>

                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div>
                      <span className="text-gray-400 text-xs">Submitted</span>
                      <p>{format(new Date(submission.submittedAt), 'MMM d, yyyy HH:mm')}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-xs">Reward</span>
                      <p>{task ? `${task.reward.amount} ${task.reward.currency}` : 'N/A'}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => handleViewSubmission(submission)}
                    className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm transition duration-200"
                  >
                    Review Submission
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">
                  {currentTask ? 'Edit Task' : 'Create New Task'}
                </h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  &times;
                </button>
              </div>

              <form onSubmit={handleSubmitTask}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Title</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    ></textarea>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Task Type</label>
                      <select
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="tweet">Tweet</option>
                        <option value="referral" disabled>Referral (Coming Soon)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Max Participants</label>
                      <input
                        type="number"
                        name="maxParticipants"
                        value={formData.maxParticipants}
                        onChange={handleInputChange}
                        min="1"
                        className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Reward Amount</label>
                      <input
                        type="number"
                        name="reward.amount"
                        value={formData.reward.amount}
                        onChange={handleInputChange}
                        min="0"
                        className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Reward Currency</label>
                      <select
                        name="reward.currency"
                        value={formData.reward.currency}
                        onChange={handleInputChange}
                        className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="BAI">BAI</option>
                        <option value="BITP">BITP</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Start Date</label>
                      <input
                        type="datetime-local"
                        value={formData.startDate ? new Date(formData.startDate).toISOString().slice(0, 16) : ''}
                        onChange={(e) => handleDateChange('startDate', new Date(e.target.value))}
                        className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">End Date</label>
                      <input
                        type="datetime-local"
                        value={formData.endDate ? new Date(formData.endDate).toISOString().slice(0, 16) : ''}
                        onChange={(e) => handleDateChange('endDate', new Date(e.target.value))}
                        className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                  </div>

                  {formData.type === 'tweet' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-1">Tweet URL</label>
                        <input
                          type="url"
                          name="tweetUrl"
                          value={formData.tweetUrl}
                          onChange={handleInputChange}
                          className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">Tweet Author</label>
                        <input
                          type="text"
                          name="tweetAuthor"
                          value={formData.tweetAuthor}
                          onChange={handleInputChange}
                          className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Requirements</label>
                        <div className="space-y-2">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              name="requirements.like"
                              checked={formData.requirements.like}
                              onChange={handleInputChange}
                              className="mr-2"
                            />
                            Like the tweet
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              name="requirements.retweet"
                              checked={formData.requirements.retweet}
                              onChange={handleInputChange}
                              className="mr-2"
                            />
                            Retweet
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              name="requirements.quote"
                              checked={formData.requirements.quote}
                              onChange={handleInputChange}
                              className="mr-2"
                            />
                            Quote tweet
                          </label>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Notification option */}
                  <div className="mt-4 border-t border-gray-700 pt-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="notifyBotUsers"
                        checked={formData.notifyBotUsers}
                        onChange={handleInputChange}
                        className="mr-2 h-4 w-4"
                      />
                      <span className="text-sm font-medium">
                        Send notification to Telegram bot users
                      </span>
                    </label>
                    <p className="text-xs text-gray-400 mt-1 ml-6">
                      When enabled, all users of the Telegram bot will receive a notification about this new task
                    </p>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition duration-200"
                    >
                      {currentTask ? 'Update Task' : 'Create Task'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Submission Review Modal */}
      {showSubmissionModal && currentSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Review Submission</h2>
                <button
                  onClick={() => setShowSubmissionModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  &times;
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-400">User</h3>
                  <p>{currentSubmission.username || `User ${currentSubmission.userId.slice(0, 8)}`}</p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-400">Task</h3>
                  <p>{tasks.find(t => t._id === currentSubmission.taskId)?.title || 'Unknown Task'}</p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-400">Submitted At</h3>
                  <p>{format(new Date(currentSubmission.submittedAt), 'MMM d, yyyy HH:mm')}</p>
                </div>

                {currentSubmission.notes && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-400">User Notes</h3>
                    <p>{currentSubmission.notes}</p>
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-medium text-gray-400">Proof Image</h3>
                  <div className="mt-2 bg-gray-900 rounded-lg p-2 text-center">
                    <img
                      src={currentSubmission.proofImageUrl}
                      alt="Proof"
                      className="max-w-full max-h-[400px] mx-auto rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-400">Admin Notes</h3>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows="3"
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mt-1"
                    placeholder="Add notes about this submission (optional)"
                  ></textarea>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={handleRejectSubmission}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition duration-200"
                  >
                    Reject
                  </button>
                  <button
                    onClick={handleApproveSubmission}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition duration-200"
                  >
                    Approve
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTasks;
